/**
 * 路径处理工具
 * 参考：pro-components/src/layout/utils/pathTools.ts
 */

import type { ProRouteObject } from '../typing';

/**
 * 标准化路径（移除重复斜杠、末尾斜杠）
 */
export function normalizePath(path: string): string {
  return path.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
}

/**
 * 拼接路径
 */
export function joinPath(...paths: string[]): string {
  const joined = paths
    .filter(Boolean)
    .join('/')
    .replace(/\/+/g, '/');

  return normalizePath(joined);
}

/**
 * 生成唯一的页签 key
 */
export function generateTabKey(pathname: string, search: string = ''): string {
  return `${pathname}${search}`;
}

/**
 * 判断路径是否匹配（支持动态参数）
 */
function matchPathPattern(pattern: string, path: string): boolean {
  const normalizedPattern = normalizePath(pattern);
  const normalizedPath = normalizePath(path);

  if (normalizedPattern === '/' || normalizedPath === '/') {
    return normalizedPattern === normalizedPath;
  }

  const patternSegments = normalizedPattern.split('/').filter(Boolean);
  const pathSegments = normalizedPath.split('/').filter(Boolean);

  if (patternSegments.length !== pathSegments.length) return false;

  return patternSegments.every((segment, index) => {
    const current = pathSegments[index];
    return segment.startsWith(':') || segment === current;
  });
}

/**
 * 查找匹配的路由配置（递归查找，支持动态路由）
 * @param pathname 当前路径
 * @param routes 路由配置数组
 * @param parentPath 父级路径（用于拼接子路由）
 * @returns 匹配的路由配置，未找到返回 null
 */
export function findMatchedRoute(
  pathname: string,
  routes?: ProRouteObject[],
  parentPath: string = ''
): ProRouteObject | null {
  if (!routes || routes.length === 0) return null;

  for (const route of routes) {
    const routePath = route.path ?? '';
    const fullPath = routePath.startsWith('/')
      ? routePath
      : joinPath(parentPath || '/', routePath || '');

    // 精确/动态匹配
    if (matchPathPattern(fullPath, pathname)) {
      return route;
    }

    // 递归查找子路由
    if (route.children) {
      const childRoute = findMatchedRoute(pathname, route.children, fullPath);
      if (childRoute) return childRoute;
    }
  }

  return null;
}

/**
 * 根据路由配置计算缓存 Key
 * @param route 路由配置
 * @param pathname 当前路径
 * @param search 查询参数
 * @returns cacheKey 和 cacheName
 */
export function getCacheKeyFromRoute(
  route: ProRouteObject,
  pathname: string,
  search: string = ''
): { cacheKey: string; cacheName: string } {
  const isDynamicRoute = route.path?.includes(':');

  let cacheKey: string;
  if (isDynamicRoute) {
    // 动态路由：每个 URL 独立缓存（包含 query 参数）
    cacheKey = `${pathname}${search}`;
  } else {
    // 静态路由：只用 pathname 作为 key，复用缓存
    cacheKey = pathname;
  }

  const cacheName = route.name || cacheKey;

  return { cacheKey, cacheName };
}
